import { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import authEvents from "../services/users/AuthEvents";

const LayoutContext = createContext();

export const useLayout = () => useContext(LayoutContext);

export function LayoutProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialSidebarOpen = () => {
    try {
      const raw = localStorage.getItem("sidebarOpen");
      return raw === null ? true : JSON.parse(raw);
    } catch {
      return true;
    }
  };

  const getInitialSubMenuOpen = () => {
    try {
      const raw = localStorage.getItem("subMenuOpen");
      if (raw === null) return [];
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return [];
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(getInitialSidebarOpen);
  const [subMenuOpen, setSubMenuOpen] = useState(getInitialSubMenuOpen);

  const [tabs, setTabs] = useState(() => {
    const saved = localStorage.getItem("tabs");
    return saved ? JSON.parse(saved) : [];
  });

  const [activeTabLink, setActiveTabLink] = useState(() => {
    const activeLink = localStorage.getItem("currentPageLink") || "";
    return tabs.some((tab) => tab.link === activeLink)
      ? activeLink
      : tabs[tabs.length - 1]?.link || null;
  });

  useEffect(() => {
    try {
      localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));
    } catch { }
  }, [sidebarOpen]);

  useEffect(() => {
    try {
      localStorage.setItem("subMenuOpen", JSON.stringify(subMenuOpen));
    } catch { }
  }, [subMenuOpen]);

  useEffect(() => {
    localStorage.setItem("tabs", JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    if (activeTabLink) {
      localStorage.setItem("currentPageLink", activeTabLink);
    } else {
      localStorage.removeItem("currentPageLink");
    }
  }, [activeTabLink]);

  useEffect(() => {
    if (tabs.some((tab) => tab.link === location.pathname)) {
      setActiveTabLink(location.pathname);
    }
  }, [location.pathname, tabs]);

  // Logout doesn't remount this provider (AppRoutes never re-renders after
  // its initial mount), so the live state has to be reset explicitly here
  // rather than relying on fresh useState initializers.
  useEffect(() => {
    const unsubscribe = authEvents.subscribe((event) => {
      if (event === "LOGGED_OUT") {
        setSidebarOpen(true);
        setSubMenuOpen([]);
        setTabs([]);
        setActiveTabLink(null);
        localStorage.removeItem("currentPageLink");
      }
    });
    return unsubscribe;
  }, []);

  const openTab = (name, link) => {
    setTabs((prev) => (prev.some((tab) => tab.link === link) ? prev : [...prev, { name, link }]));
    setActiveTabLink(link);
  };

  const closeTab = (link) => {
    const newTabs = tabs.filter((tab) => tab.link !== link);
    setTabs(newTabs);
    if (newTabs.length === 0) {
      setActiveTabLink(null);
      navigate("/homepage");
      localStorage.removeItem("currentPageLink");
    } else if (link === activeTabLink) {
      const lastTab = newTabs[newTabs.length - 1];
      setActiveTabLink(lastTab.link);
      navigate(lastTab.link);
    }
  };

  const handleTabClick = (link) => {
    setActiveTabLink(link);
    navigate(link);
  };

  return (
    <LayoutContext.Provider
      value={{
        sidebarOpen,
        setSidebarOpen,
        subMenuOpen,
        setSubMenuOpen,
        tabs,
        activeTabLink,
        openTab,
        closeTab,
        handleTabClick,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}
